import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';

/**
 * Component for selecting different passenger categories
 * Allows selecting Adults, Seniors, Minors, and Infants with +/- controls
 */
const PassengerCategorySelector = ({ 
  maxPassengers, 
  onChange,
  initialValues = {
    adults: 1,
    seniors: 0,
    minors: 0,
    infants: 0
  }
}) => {
  const [passengers, setPassengers] = useState(initialValues);
  
  // Calculate total passengers
  const totalPassengers = Object.values(passengers).reduce(
    (sum, count) => sum + (Number(count) || 0),
    0
  );
  
  useEffect(() => {
    if (onChange) {
      onChange(passengers, totalPassengers);
    }
  }, [passengers, onChange]);

  // Handle increment/decrement for each passenger type
  const handlePassengerChange = (type, increment) => {
    setPassengers(prev => {
      // Don't allow negative values
      if (prev[type] + increment < 0) return prev;
      
      // Don't exceed max passengers
      const newTotal = totalPassengers + increment;
      if (newTotal > maxPassengers && increment > 0) return prev;
      
      // Update the passenger count
      return {
        ...prev,
        [type]: prev[type] + increment
      };
    });
  };

  return (
    <div className="passenger-category-selector">
      {/* <h5 className="mb-3" style={{ textAlign: 'center' }}>Group Size</h5> */}
      
      <div className="progress mb-4">
        <div 
          className="progress-bar bg-dark" 
          role="progressbar" 
          style={{ width: `${(totalPassengers / maxPassengers) * 100}%` }}
          aria-valuenow={totalPassengers}
          aria-valuemin="0"
          aria-valuemax={maxPassengers}
        ></div>
      </div>
      
      {/* Adults */}
      <Row className="d-flex justify-content-center align-items-center gap-4 mb-3">
        <Form.Label className="inquiry-label" style={{ textAlign: 'center', marginBottom: '0' }}>
          Adults <small>(18 and older)</small>
        </Form.Label>
        <div className="d-flex justify-content-center align-items-center gap-3">
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('adults', -1)}
            disabled={passengers.adults <= 1}
          >
            <i className="material-icons add-remove">remove_circle</i>
          </Button>
          <Form.Control
            type="number"
            name="adults"
            value={passengers.adults}
            className="form-control text-center"
            style={{ width: '120px' }}
            readOnly
          />
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('adults', 1)}
            disabled={totalPassengers >= maxPassengers}
          >
            <i className="material-icons add-remove">add_circle</i>
          </Button>
        </div>
      </Row>
      
      {/* Seniors */}
      <Row className="d-flex justify-content-center align-items-center gap-4 mb-3">
        <Form.Label className="inquiry-label" style={{ textAlign: 'center', marginBottom: '0' }}>
          Seniors <small>(65 and older)</small>
        </Form.Label>
        <div className="d-flex justify-content-center align-items-center gap-3">
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('seniors', -1)}
            disabled={passengers.seniors <= 0}
          >
            <i className="material-icons add-remove">remove_circle</i>
          </Button>
          <Form.Control
            type="number"
            name="seniors"
            value={passengers.seniors}
            className="form-control text-center"
            style={{ width: '120px' }}
            readOnly
          />
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('seniors', 1)}
            disabled={totalPassengers >= maxPassengers}
          >
            <i className="material-icons add-remove">add_circle</i>
          </Button>
        </div>
      </Row>
      
      {/* Minors */}
      <Row className="d-flex justify-content-center align-items-center gap-4 mb-3">
        <Form.Label className="inquiry-label" style={{ textAlign: 'center', marginBottom: '0' }}>
          Minors <small>(Younger than 18)</small>
        </Form.Label>
        <div className="d-flex justify-content-center align-items-center gap-3">
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('minors', -1)}
            disabled={passengers.minors <= 0}
          >
            <i className="material-icons add-remove">remove_circle</i>
          </Button>
          <Form.Control
            type="number"
            name="minors"
            value={passengers.minors}
            className="form-control text-center"
            style={{ width: '120px' }}
            readOnly
          />
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('minors', 1)}
            disabled={totalPassengers >= maxPassengers}
          >
            <i className="material-icons add-remove">add_circle</i>
          </Button>
        </div>
      </Row>
      
      {/* Infants */}
      <Row className="d-flex justify-content-center align-items-center gap-4 mb-3">
        <Form.Label className="inquiry-label" style={{ textAlign: 'center', marginBottom: '0' }}>
          Infants <small>(Younger than 1 year old)</small>
        </Form.Label>
        <div className="d-flex justify-content-center align-items-center gap-3">
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('infants', -1)}
            disabled={passengers.infants <= 0}
          >
            <i className="material-icons add-remove">remove_circle</i>
          </Button>
          <Form.Control
            type="number"
            name="infants"
            value={passengers.infants}
            className="form-control text-center"
            style={{ width: '120px' }}
            readOnly
          />
          <Button
            className="d-flex justify-content-center align-items-center"
            style={{ width: '30px' }}
            onClick={() => handlePassengerChange('infants', 1)}
            disabled={totalPassengers >= maxPassengers}
          >
            <i className="material-icons add-remove">add_circle</i>
          </Button>
        </div>
      </Row>
      
      <h5
        style={{
          textAlign: 'center',
          marginTop: '20px',
          borderTop: '1px dashed #a3aeb2',
          padding: '13px 0 0',
          width: '50%',
          margin: '20px auto 0',
        }}
      >
        Total: {totalPassengers}
      </h5>
      {totalPassengers >= maxPassengers && 
        <p className="text-danger text-center mt-2">Maximum capacity reached</p>
      }
    </div>
  );
};

export default PassengerCategorySelector;
